import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ProviderPriceListModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/providerPriceList/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerPriceList/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerPriceList/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerPriceList/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerPriceList/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerPriceList/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/providerPriceList/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/providerPriceList/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/providerPriceList/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerPriceList/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerPriceList/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerPriceList/DELETE_FAILURE'

export type ProviderPriceListModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ProviderPriceListModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderPriceLists(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().providerPriceLists.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_price_lists.json', options)
      .then(({ data }: { data: ProviderPriceListModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadProviderPriceList(providerPriceListId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.providerPriceLists.loadedForKeys]
    const loadedIds = [...state.providerPriceLists.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (providerPriceListId && !loadedIds.includes(providerPriceListId)){
      loadedIds.push(providerPriceListId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/provider_price_lists/${providerPriceListId}.json`, options)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createProviderPriceList(
  providerPriceList: Partial<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_price_list: providerPriceList,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_price_lists.json', options, config)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function forkProviderPriceList(
  forkPriceListParams: { agency_id?: number, hide_items?: boolean, providerPriceListId: number, title: string },
  options: EntityOptions = {},
) {
  const {
    agency_id, hide_items, providerPriceListId, title,
  } = forkPriceListParams

  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_id,
      hide_items,
      title,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/provider_price_lists/${providerPriceListId}/fork.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function unarchiveProviderPriceList(
  providerPriceList: UpdateParams<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list_id: providerPriceList.id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_lists/${providerPriceList.id}/unarchive.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateProviderPriceList(
  providerPriceList: UpdateParams<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_price_list_id: providerPriceList.id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_lists/${providerPriceList.id}/duplicate.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateProviderPriceList(
  providerPriceList: UpdateParams<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list: providerPriceList,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_lists/${providerPriceList.id}.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export type BulkUpdateParams = {
  expires_at: string,
  percentage: number,
  price_list_ids: number[],
  undo_change: boolean,
}

export function bulkUpdateProviderPriceLists(
  bulkUpdateParams: BulkUpdateParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(bulkUpdateParams),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/provider_price_lists/bulk_update.json', options, config)
      .then(({ data }: { data: ProviderPriceListModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))
        return { success: false as const, errors }
      })

    return promise
  }
}

export function syncChangesToForks(
  providerPriceList: ProviderPriceListModel,
  options: EntityOptions = {},
) {
  const { id } = providerPriceList

  const config = {
    method: 'PUT',
  }

  return () => {
    const promise = api(`/provider_price_lists/${id}/sync_changes_to_forks.json`, options, config)
      .then(({ data }: { data: object }) => ({ success: true as const, data }))
      .catch((data) => {
        const errors = handleError(data)
        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteProviderPriceList(
  providerPriceList: DeleteParams<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      provider_price_list: providerPriceList,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_price_lists/${providerPriceList.id}.json`, options, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteItemsForProduct(
  deleteParams: { providerPriceListId: number, serviceProductId?: number },
  options: EntityOptions = {},
) {
  const { providerPriceListId, serviceProductId } = deleteParams

  const config = {
    method: 'DELETE',
    data: JSON.stringify({
      service_product_id: serviceProductId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_price_lists/${providerPriceListId}/destroy_items_for_product.json`, options, config)
      .then(({ data }: { data: { ids: number[] } }) => {
        // Loop through each id and remove from store
        data.ids.forEach((id) => {
          const normalizedJson = normalize({ id }, Schemas.PROVIDER_PRICE_LIST_ITEM)
          dispatch(deleteEntity(normalizedJson))
        })

        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkReassignAgencies(
  providerPriceList: Partial<ProviderPriceListModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list: providerPriceList,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_lists/${providerPriceList.id}/bulk_reassign_agencies.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function adjustPriceByPercentage(
  adjustPriceParams: {
    percentage: number, undo_change: boolean, round_to_nearest_dollar: boolean, providerPriceListId: number,
  },
  options: EntityOptions = {},
) {
  const {
    percentage, undo_change, round_to_nearest_dollar, providerPriceListId,
  } = adjustPriceParams

  const config = {
    method: 'POST',
    data: JSON.stringify({
      percentage,
      undo_change,
      round_to_nearest_dollar,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_lists/${providerPriceListId}/adjust_prices_by_percentage.json`, options, config)
      .then(({ data }: { data: ProviderPriceListModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ProviderPriceListModuleState = initialState,
  action: Action = { type: '' },
): ProviderPriceListModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
